import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  lazy,
  useRef,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { ImageWithFallback } from "./components/figma/ImageWithFallback";
import { NoInternetOverlay } from "./components/NoInternetOverlay";
import { SandboxSessionProvider } from "./contexts/SandboxSessionContext";
import { SubmissionNavigationProvider } from "./contexts/SubmissionNavigationContext";
import { NetworkStatusProvider } from "./contexts/NetworkStatusContext";
import { ROLES, UserRole, normalizeUserRole } from "./constant/roles";
import { User, Eye, EyeOff } from "lucide-react";
import StudentDashboard from "./pages/StudentDashboard";
import MyCoursesPage from "./pages/MyCoursesPage";
import InstructorDashboard from "./pages/InstructorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ProjectDetailsPage from "./pages/ProjectDetailsPage";
import SubmissionsPage from "./pages/SubmissionsPage";
import CoursesPage from "./pages/CoursesPage";
import ViewCoursePage from "./pages/ViewCoursePage";
import ViewAllProjectsPage from "./pages/ViewAllProjectsPage";
import InstructorProjectDetailsPage from "./pages/InstructorProjectDetailsPage";
import ViewStudentPage from "./pages/ViewStudentPage";
import GradebookPage from "./pages/GradebookPage";
import ThemeSettings from "./pages/ThemeSettings";
// import GradeDetailsPage from "./components/GradeDetailsPage";
import AdminProjectsPage from "./pages/AdminProjectsPage";
import AdminReportsPage from "./pages/AdminReportsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AuditPage from "./pages/AuditPage";
import CommunicationPage from "./pages/CommunicationPage";
import SandboxEnvironmentPage from "./pages/SandboxEnvironmentPage";
import SandboxManagementPage from "./pages/SandboxManagementPage";
import UserManagementPage from "./pages/UserManagementPage";
import GradingPage from "./pages/GradingPage";
import EnhancedSubmissionDetailsPage from "./pages/EnhancedSubmissionDetailsPage";
// import SubmissionDetailsPage from "./components/SubmissionDetailsPage";
import ProjectManagementPage from "./pages/ProjectManagementPage";
import CreateProjectPage from "./pages/CreateProjectPage";
import ModelTestingPage from "./pages/ModelTestingPage";
import NotebookPage from "./pages/NotebookPage";
import QuizLandingPage from "./pages/QuizLandingPage";
import { useProjectLink } from "./contexts/ProjectLinkContext";
import CodeViewerPage from "./pages/CodeViewerPage";
import Header from "./components/Header";
import RolesPermissionsListPage from "./pages/RolesPermissionsListPage";
import SandboxAnalyticsDashboard from "./pages/SandboxAnalyticsDashboard";
import TimeExtensionManagement from "./components/TimeExtensionManagement";
import { useLoginQuery, logoutUser } from "./api/authApi";
import { tokenService } from "./services/TokenService";
import { getPrimaryRoleName } from "./utils/authUtils";
import { useResourceProfileByIdQuery } from "./api/sandboxManagementApi";
import { useAxiosAuth } from "./hooks/useAxiosAuth";
import paths from "./routes";
import studentRoutes from "./StudentRoutes";
import AuthCallbackPage from "./components/AuthCallbackPage";
import MoodleCallbackPage from "./pages/MoodleCallbackPage";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { BASE_URL } from "./constant/config";
import { API_CONFIG } from "./config/app.config";
import Logo from "./assets/data-lab-logo.png";
import MainSidebar from "./components/MainSidebar/MainSidebar";
import { getConfiguration } from "./api/settingsApi";
import ManageDataSets from "./pages/ManageDataSets";
import ViewDatasetPage from "./pages/ViewDatasetPage";
import {
  PracticeProjectsManagementPage,
  PracticeProjectsBrowsePage,
  MyPracticeProjectsPage,
  PracticeProjectDetailsPage,
  CreatePracticeProjectPage,
  PracticeProjectWorkspacePage,
} from "./pages/PracticeProjects";
import AIChatPage from "./pages/AIChatPage";
const PageNotFound = lazy(() => import("./pages/PageNotFound"));

// Helper function to get default path for each role
const getDefaultPathForRole = (role: UserRole): string => {
  switch (role) {
    case ROLES.STUDENT:
      return "/"; // Student dashboard
    case ROLES.INSTRUCTOR:
    case ROLES.TA:
      return "/"; // Instructor dashboard
    case ROLES.ADMIN:
      return "/"; // Admin dashboard
    default:
      return "/";
  }
};

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string, role: UserRole) => Promise<boolean>;
  logout: () => void;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  startResourceProfilePolling: (profileId: string, profileName: string) => void;
}

interface NavigationContextType {
  currentPage: string;
  navigateTo: (page: string, params?: any) => void;
  pageParams: any;
  goBack: () => void;
  navigationHistory: Array<{ page: string; params: any }>;
  isSidebarVisible: boolean;
  setSidebarVisible: (visible: boolean) => void;
}

interface UtilityContextType {
  downloadSubmission: (submissionId: string, submissionData: any) => void;
  generateSubmissionZip: (submissionData: any) => Promise<Blob>;
}

// Mock users for demo
const mockUsers: { [key: string]: User } = {
  "rahul.t@bits.com": {
    id: "1",
    name: "sharma",
    email: "rahul.t@bits.com",
    role: ROLES.STUDENT,
  },
  "vikram.d@bits.com": {
    id: "4",
    name: "rohan",
    email: "vikram.d@bits.com",
    role: ROLES.STUDENT,
  },
  "priya.k@bits.com": {
    id: "2",
    name: "Dr. A. Sharma",
    email: "priya.k@bits.com",
    role: ROLES.INSTRUCTOR,
  },
  "arun.kumar@bits.com": {
    id: "3",
    name: "Admin User",
    email: "arun.kumar@bits.com",
    role: ROLES.ADMIN,
  },
};

// Demo credentials for easy login
const demoCredentials = [
  {
    title: "Admin User",
    email: "arun.kumar@bits.com",
    password: "123456",
    role: ROLES.ADMIN as UserRole,
  },
  {
    title: "Instructor",
    email: "priya.k@bits.com",
    password: "123456",
    role: ROLES.INSTRUCTOR as UserRole,
  },
  {
    title: "Sharma",
    email: "rahul.t@bits.com",
    password: "123456",
    role: ROLES.STUDENT as UserRole,
  },
  {
    title: "Rohan",
    email: "vikram.d@bits.com",
    password: "123456",
    role: ROLES.STUDENT as UserRole,
  },
];

// Auth Context
const AuthContext = createContext<AuthContextType | null>(null);

// Navigation Context
const NavigationContext = createContext<NavigationContextType | null>(null);

// Utility Context
const UtilityContext = createContext<UtilityContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};

export const useUtilities = () => {
  const context = useContext(UtilityContext);
  if (!context) {
    throw new Error("useUtilities must be used within a UtilityProvider");
  }
  return context;
};

const downloadSubmission = (submissionId: string, submissionData: any) => {
  generateSubmissionZip(submissionData)
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `submission_${submissionId}_${
        submissionData.assignmentTitle || "assignment"
      }.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Submission downloaded successfully");
    })
    .catch((error) => {
      console.error("Download failed:", error);
      toast.error("Failed to download submission");
    });
};

const generateSubmissionZip = async (submissionData: any): Promise<Blob> => {
  // In a real implementation, this would use a library like JSZip
  // For now, we'll create a mock zip file with submission metadata
  const mockZipContent = {
    metadata: {
      submissionId: submissionData.id,
      assignmentTitle: submissionData.assignmentTitle,
      studentName: submissionData.studentName,
      submissionDate: submissionData.submissionDate,
      status: submissionData.status,
      grade: submissionData.grade,
      feedback: submissionData.feedback,
    },
    files: submissionData.files || [],
    notebooks: submissionData.notebooks || [],
    datasets: submissionData.datasets || [],
  };

  // Create a JSON file as a placeholder for the actual zip
  const jsonContent = JSON.stringify(mockZipContent, null, 2);
  return new Blob([jsonContent], { type: "application/json" });
};

// Login Component
function LoginForm({ data }: { data?: any } = {}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>(ROLES.STUDENT as UserRole);
  const [loading, setLoading] = useState(false);
  const { login, setUser } = useAuth();
  const loginQuery = useLoginQuery({ email, password });
  const location = useLocation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  // Handle error messages from URL params or navigation state
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlError = urlParams.get("error");
    const stateError = location.state?.error;

    if (urlError) {
      const errorMessage = decodeURIComponent(urlError);
      toast.error(errorMessage);
    } else if (stateError) {
      toast.error(stateError);
      // Clear the error from state to prevent showing it again on refresh
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use backend login first
      const result = await loginQuery.refetch();
      const resp = result.data as any;

      if (resp?.isSuccess && resp?.data?.accessToken && resp?.data?.user) {
        // Save token with expiry for subsequent requests
        tokenService.setToken(resp.data.accessToken, resp.data.expiresIn);
        const roleName = getPrimaryRoleName(resp.data.user.roles);
        const mappedRole = normalizeUserRole(roleName);
        const newUser: User = {
          id: resp.data.user.id,
          name: resp.data.user.name,
          email: resp.data.user.email,
          role: mappedRole as UserRole,
        };
        setUser(newUser);
        try {
          localStorage.setItem("auth_user", JSON.stringify(newUser));
        } catch {}

        toast.success("Login successful!");

        // Use backend-provided path if available, otherwise use role-based default
        const redirectPath =
          resp.data.redirectPath ||
          resp.data.defaultPath ||
          getDefaultPathForRole(mappedRole as UserRole);
        navigate(redirectPath, { replace: true });
      } else {
        // Fallback to demo login if backend says invalid
        const success = await login(email, password, role);
        if (success) {
          toast.success("Login successful!");
          // For demo users, use role-based default paths
          const demoRedirectPath = getDefaultPathForRole(role);
          navigate(demoRedirectPath, { replace: true });
        } else {
          toast.error(
            resp?.message || "Invalid credentials. Please try again.",
          );
        }
      }
    } catch (error) {
      toast.error("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    const imageUrl = API_CONFIG.image_url;
    if (imageUrl) {
      window.location.href = imageUrl;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f8fa]">
      <div className="flex space-x-4 bg-white p-3">
        <div
          className="flex space-x-3 cursor-pointer"
          onClick={handleLogoClick}
        >
          <div className="w-20 h-16 relative">
            <ImageWithFallback
              src={data?.data?.logoUrl || Logo}
              alt="Data Lab Logo"
              className="w-[50px] h-[50px] object-contain zoom-in"
            />
          </div>
          {/* <div className="h-12 w-px bg-border mx-1" aria-hidden="true" /> */}
          {/* <div className="w-20 h-12 relative">
            <ImageWithFallback
              src={D2Llogo}
              alt="D2L Logo"
              className="w-full h-full object-contain zoom-in"
            />
          </div> */}
        </div>
      </div>
      <div className="flex flex-col h-screen sm:flex-row flex-nowrap items-center justify-center gap-6">
        {/* Login Form Card */}
        <div className="w-full max-w-md">
          <Card className="w-full bg-white rounded-2xl shadow-lg p-4 border border-border">
            <CardHeader className="text-center">
              <div className="mb-4">
                <div className="w-30 h-20 mx-auto relative">
                  <ImageWithFallback
                    src={data?.data?.logoUrl || Logo}
                    alt="Data Lab Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <CardDescription className="text-xl font-semibold text-gray-800">
                {data?.data?.institutionName || "Data Lab"}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-lg py-2 shadow-sm text-base font-semibold flex items-center gap-2"
                  onClick={() =>
                    (window.location.href = `${BASE_URL}/api/auth/google`)
                  }
                  disabled={loading}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-lg py-2 shadow-sm text-base font-semibold flex items-center gap-2"
                  onClick={() =>
                    (window.location.href = `${BASE_URL}/api/auth/moodle`)
                  }
                  disabled={loading}
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#F98012" />
                    <path
                      d="M2 17L12 22L22 17V12L12 17L2 12V17Z"
                      fill="#F98012"
                    />
                  </svg>
                  Sign in with Moodle
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-left">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="bg-muted rounded-lg text-muted-foreground border-none focus:ring-2 focus:ring-bits-blue focus:border-bits-blue"
                  />
                </div>
                <div className="space-y-2 text-left">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="bg-muted rounded-lg text-muted-foreground border-none focus:ring-2 focus:ring-bits-blue focus:border-bits-blue"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-2 text-left">
                  <Label htmlFor="role" className="text-sm font-medium">
                    Role
                  </Label>
                  <Select
                    value={role}
                    onValueChange={(value: UserRole) => setRole(value)}
                  >
                    <SelectTrigger className="bg-muted rounded-lg border-none focus:ring-2 focus:ring-bits-blue focus:border-bits-blue">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ROLES.STUDENT}>Student</SelectItem>
                      <SelectItem value={ROLES.INSTRUCTOR}>
                        Instructor
                      </SelectItem>
                      <SelectItem value={ROLES.ADMIN}>Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  variant="bits"
                  className="w-full rounded-lg py-2 mt-4 shadow-sm text-base font-semibold"
                  disabled={loading}
                >
                  {loading ? "Logging in..." : "Log In"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function NotebookRoute() {
  const { state } = useLocation() as {
    state?: { project?: { id: string; title?: string } };
  };
  return <NotebookPage project={state?.project} />;
}

// Main Dashboard Router
function DashboardRouter() {
  const { user, isLoading } = useAuth();
  const { pageParams, goBack } = useNavigation();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bits-blue"></div>
      </div>
    );
  }

  if (!user) return <Navigate to={paths.signIn} replace />;

  //{ path: paths.notebook, element: <NotebookPage />, name: "Notebook" }
  return (
    <Routes>
      <Route
        path="*"
        element={
          <Routes>
            {/* Common routes */}
            <Route
              path={paths.projectDetails}
              element={<ProjectDetailsPage />}
            />
            <Route path={paths.courses} element={<CoursesPage />} />
            <Route path={paths.myCourses} element={<MyCoursesPage />} />
            <Route
              path={paths.viewCourse}
              element={<ViewCoursePage courseId={pageParams?.courseId} />}
            />

            <Route
              path={paths.viewStudent}
              element={<ViewStudentPage studentId={pageParams?.studentId} />}
            />
            <Route path={paths.gradebook} element={<GradebookPage />} />
            <Route path={paths.notebook} element={<NotebookRoute />} />
            <Route
              path={paths.communication}
              element={
                <CommunicationPage defaultTab={pageParams?.defaultTab} />
              }
            />
            <Route
              path={paths.submissions}
              element={
                <SubmissionsPage assignmentId={pageParams?.assignmentId} />
              }
            />

            {/* Practice Projects - Common Routes */}
            <Route
              path={paths.practiceProjects}
              element={<PracticeProjectsBrowsePage />}
            />
            <Route
              path={paths.practiceProjectDetails}
              element={<PracticeProjectDetailsPage />}
            />
            <Route
              path={paths.myPracticeProjects}
              element={<MyPracticeProjectsPage />}
            />
            <Route
              path={paths.practiceProjectWorkspace}
              element={<PracticeProjectWorkspacePage />}
            />

            {/* Student-only routes */}
            {user.role === ROLES.STUDENT &&
              studentRoutes.map(({ path, element, name, index }) => {
                const routeProps: any = { element };
                if (path) routeProps.path = path;
                if (index) routeProps.index = index;
                return <Route key={name} {...routeProps} />;
              })}

            {user.role === ROLES.STUDENT && (
              <Route index element={<StudentDashboard />} />
            )}

            <Route path={paths.quizLanding} element={<QuizLandingPage />} />

            {(user.role === ROLES.INSTRUCTOR ||
              user.role === ROLES.ADMIN ||
              user.role === ROLES.TA) && (
              <>
                <Route
                  path={paths.timeExtensions}
                  element={<TimeExtensionManagement userRole={user.role} />}
                />
                <Route
                  path={paths.studentNotebook}
                  element={
                    <SandboxEnvironmentPage
                      mode="student-notebook"
                      studentId={pageParams?.studentId}
                      studentName={pageParams?.studentName}
                      assignmentId={pageParams?.assignmentId}
                      assignmentTitle={pageParams?.assignmentTitle}
                      notebookUrl={pageParams?.notebookUrl}
                    />
                  }
                />
                <Route
                  path={paths.viewAllProjects}
                  element={<ViewAllProjectsPage />}
                />
                <Route
                  path={paths.instructorProjectDetails}
                  element={<InstructorProjectDetailsPage />}
                />
                <Route
                  path={paths.studentSubmissions}
                  element={<InstructorProjectDetailsPage />}
                />

                <Route
                  path={paths.modelTesting}
                  element={
                    <ModelTestingPage
                      studentId={pageParams?.studentId}
                      studentName={pageParams?.studentName}
                      assignmentId={pageParams?.assignmentId}
                      assignmentTitle={pageParams?.assignmentTitle}
                      submissionId={pageParams?.submissionId}
                      onBack={goBack}
                    />
                  }
                />
                <Route
                  path={paths.viewStudentCode}
                  element={
                    <CodeViewerPage
                      studentId={pageParams?.studentId}
                      studentName={pageParams?.studentName}
                      assignmentId={pageParams?.assignmentId}
                      assignmentTitle={pageParams?.assignmentTitle}
                      submissionId={pageParams?.submissionId}
                    />
                  }
                />
                <Route
                  path={paths.submissionDetails}
                  element={
                    <SubmissionNavigationProvider>
                      <EnhancedSubmissionDetailsPage />
                    </SubmissionNavigationProvider>
                  }
                />

                <Route
                  path={paths.userManagement}
                  element={
                    user?.role === ROLES.ADMIN ? (
                      <UserManagementPage />
                    ) : (
                      <Navigate to={paths.root} replace />
                    )
                  }
                />
                <Route
                  path={paths.sandboxManagement}
                  element={<SandboxManagementPage />}
                />
                <Route
                  path={paths.gradingSandbox}
                  element={<SandboxEnvironmentPage mode="grading" />}
                />
                <Route path={paths.grading} element={<GradingPage />} />
                <Route
                  path={paths.createProject}
                  element={<CreateProjectPage />}
                />
                <Route
                  path={paths.editProject}
                  element={<CreateProjectPage />}
                />
                <Route
                  path={paths.useTemplate}
                  element={<CreateProjectPage />}
                />
                <Route
                  path={paths.manageProjects}
                  element={<ProjectManagementPage />}
                />
                <Route path={paths.dataSet} element={<ManageDataSets />} />
                <Route path={paths.viewDataset} element={<ViewDatasetPage />} />

                {/* Practice Projects Management */}
                <Route
                  path={paths.practiceProjectsManagement}
                  element={<PracticeProjectsManagementPage />}
                />
                <Route
                  path={paths.createPracticeProject}
                  element={<CreatePracticeProjectPage />}
                />
                <Route
                  path={paths.editPracticeProject}
                  element={<CreatePracticeProjectPage />}
                />

                {(user.role === ROLES.INSTRUCTOR || user.role === ROLES.TA) && (
                  <Route index element={<InstructorDashboard />} />
                )}
              </>
            )}

            {/* Admin-only routes */}
            {user.role === ROLES.ADMIN && (
              <>
                <Route
                  path={paths.rolePermissions}
                  element={<RolesPermissionsListPage />}
                />
                <Route path={paths.settings} element={<ThemeSettings />} />
                <Route
                  path={paths.sandboxAnalytics}
                  element={<SandboxAnalyticsDashboard />}
                />
                <Route
                  path={paths.adminProjects}
                  element={<AdminProjectsPage />}
                />
                <Route
                  path={paths.adminReports}
                  element={<AdminReportsPage />}
                />
                <Route
                  path={paths.adminSettings}
                  element={<AdminSettingsPage />}
                />
                <Route path={paths.adminAudit} element={<AuditPage />} />
                <Route index element={<AdminDashboard />} />
              </>
            )}

            <Route
              path={paths.sandbox}
              element={
                <SandboxEnvironmentPage initialTab={pageParams?.initialTab} />
              }
            />

            <Route path={paths.aiChat} element={<AIChatPage />} />

            {/* Specific not found page with message support */}
            <Route path={paths.pageNotFound} element={<PageNotFound />} />

            {/* Catch-all route - only redirect truly invalid paths */}
            <Route path={paths.notFound} element={<PageNotFound />} />
          </Routes>
        }
      />
    </Routes>
  );
}

// Main App Component
export default function App() {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(() => {
    try {
      const raw = localStorage.getItem("auth_user");
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize axios auth hook for 401 handling
  useAxiosAuth();
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [pageParams, setPageParams] = useState<any>(null);
  const [navigationHistory, setNavigationHistory] = useState<
    Array<{ page: string; params: any }>
  >([]);
  const { clearProjectLinkParams } = useProjectLink();

  // Resource profile polling state
  const [pollingProfileId, setPollingProfileId] = useState<string | null>(null);
  const [pollingProfileName, setPollingProfileName] = useState<string>("");
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Poll for resource profile status
  const { data: pollingProfileData, refetch: refetchProfile } =
    useResourceProfileByIdQuery(pollingProfileId || undefined, {
      enabled: false, // We'll manually trigger refetch
      refetchInterval: false,
    });

  const configurationQuery = useQuery({
    queryKey: ["/config"],
    queryFn: getConfiguration,
    refetchOnWindowFocus: false,
  });

  const { data: configurationData } = configurationQuery;

  // Effect to handle resource profile polling
  useEffect(() => {
    if (!pollingProfileId) return;
    console.log("Starting polling for profile ID:", pollingProfileId);
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Start polling
    pollingIntervalRef.current = setInterval(async () => {
      const result = await refetchProfile();
      const profileData = result.data;
      console.log(profileData);
      if (!profileData) return;
      if (profileData?.build_status?.toLowerCase().includes("completed")) {
        // Refresh resource profiles data before clearing polling
        queryClient.invalidateQueries({ queryKey: ["resourceProfiles"] });
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        setPollingProfileId(null);
        toast.success(
          `Resource profile "${pollingProfileName}" has completed build and can be used!`,
        );
        setPollingProfileName("");
      } else if (profileData?.build_status?.toLowerCase().includes("failed")) {
        // Refresh resource profiles data before clearing polling
        queryClient.invalidateQueries({ queryKey: ["resourceProfiles"] });
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        setPollingProfileId(null);
        toast.error(
          `Resource profile "${pollingProfileName}" build failed. Please try again.`,
        );
        setPollingProfileName("");
      }
    }, 10000); // Poll every 10 seconds

    // Cleanup on unmount or when pollingProfileId changes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [pollingProfileId, pollingProfileName, refetchProfile]);

  // Function to start polling from anywhere in the app
  const startResourceProfilePolling = (
    profileId: string,
    profileName: string,
  ) => {
    setPollingProfileId(profileId);
    setPollingProfileName(profileName);
  };

  const login = async (
    email: string,
    password: string,
    role: UserRole,
  ): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const mockUser = mockUsers[email];
    if (mockUser && password === "password" && mockUser.role === role) {
      setUser(mockUser);
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (!user) return;
    const path = window.location.pathname;
    if (path.includes("/api/project/manage-project")) {
      if (user.role === ROLES.ADMIN || user.role === ROLES.INSTRUCTOR) {
        navigateTo("manage-projects");
      } else if (user.role === ROLES.STUDENT) {
        navigateTo("student-dashboard");
      }
    }
  }, [user]);
  const logout = async () => {
    try {
      // Try server logout with current token
      const resp = await logoutUser();
      if (resp?.success) {
        toast.success(resp.message || "Logged out successfully");
      }
    } catch {
      // Ignore network/API errors; proceed with local cleanup
    } finally {
      setUser(null);
      tokenService.clearToken();
      try {
        localStorage.removeItem("auth_user");
      } catch {}
      clearProjectLinkParams();
      setCurrentPage("dashboard");
      setPageParams(null);
      setNavigationHistory([]);
      navigate("/sign-in", { replace: true });
    }
  };

  const navigateTo = (page: string, params?: any) => {
    // Add current page to history before navigating (but not for sandbox launches to avoid clutter)
    if (currentPage !== "sandbox" || page !== "sandbox") {
      setNavigationHistory((prev) => [
        ...prev,
        { page: currentPage, params: pageParams },
      ]);
    }
    setCurrentPage(page);
    setPageParams(params);
  };

  const goBack = () => {
    if (navigationHistory.length > 0) {
      const previousPage = navigationHistory[navigationHistory.length - 1];
      console.log("click reached");
      setNavigationHistory((prev) => prev.slice(0, -1));
      if (previousPage) {
        setCurrentPage(previousPage.page);
        setPageParams(previousPage.params);
      } else {
        setCurrentPage("dashboard");
        setPageParams(null);
      }
    } else {
      // Default fallback to dashboard
      setCurrentPage("dashboard");
      setPageParams(null);
    }
  };

  const authValue: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    setUser,
    startResourceProfilePolling,
  };
  const [isSidebarVisible, setSidebarVisible] = useState(true);
  const navigationValue: NavigationContextType = {
    currentPage,
    navigateTo,
    pageParams,
    goBack,
    navigationHistory,
    isSidebarVisible,
    setSidebarVisible,
  };

  const utilityValue: UtilityContextType = {
    downloadSubmission,
    generateSubmissionZip,
  };

  // Listen for auth:logout events from axios interceptor
  useEffect(() => {
    const handleLogoutEvent = () => {
      console.log("App received auth:logout event, clearing user state");
      setUser(null);
      setCurrentPage("dashboard");
      setPageParams(null);
      setNavigationHistory([]);
      // Force navigation to sign-in page
      navigate("/sign-in", { replace: true });
    };

    window.addEventListener("auth:logout", handleLogoutEvent);

    return () => {
      window.removeEventListener("auth:logout", handleLogoutEvent);
    };
  }, [navigate]);

  // Initialize authentication on app load
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // If user is already logged in with a valid token, don't interfere
      const hasValidAccessToken = !!tokenService.getToken();
      if (!cancelled && user && hasValidAccessToken) {
        console.log(
          "User already authenticated with valid token, skipping refresh",
        );
        setIsLoading(false);
        return;
      }

      // Only try refresh if we have no user but might have a refresh token
      if (!cancelled && !user) {
        try {
          console.log("No user found, attempting token refresh...");
          const { refreshToken } = await import("./api/authApi");
          const refreshResp = await refreshToken();

          if (!cancelled && refreshResp?.success && refreshResp.user) {
            // Save the new access token with expiry
            tokenService.setToken(
              refreshResp.accessToken,
              refreshResp.expiresIn,
            );
            console.log(refreshResp?.user?.roles?.[0]?.name);
            const roleName = (
              refreshResp.user.roles?.[0]?.name || ROLES.STUDENT
            ).toLowerCase();
            const mappedRole = normalizeUserRole(roleName);

            const refreshedUser: User = {
              id: refreshResp.user.id,
              name: refreshResp.user.name,
              email: refreshResp.user.email,
              role: mappedRole as UserRole,
            };
            setUser(refreshedUser);
            try {
              localStorage.setItem("auth_user", JSON.stringify(refreshedUser));
            } catch {}

            console.log("Token refresh successful, user authenticated");

            // Only redirect if we're on sign-in page or auth callback
            const currentPath = window.location.pathname;
            if (
              currentPath === "/sign-in" ||
              currentPath === "/auth/callback"
            ) {
              navigate("/", { replace: true });
            }
            setIsLoading(false);
            return;
          }

          // Only redirect to sign-in if refresh failed and we have no user
          if (!cancelled && !user) {
            console.log(
              "Token refresh failed and no user, redirecting to sign-in",
            );
            tokenService.clearToken();
            setIsLoading(false);
          }
        } catch (error) {
          console.log("Token refresh error:", error);
          // Only clear state if we don't have a user (don't interfere with successful logins)
          if (!cancelled && !user) {
            tokenService.clearToken();
            setIsLoading(false);
          }
        }
      } else {
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []); // Remove navigate dependency to prevent re-running on every navigation

  return (
    <NetworkStatusProvider>
      <AuthContext.Provider value={authValue}>
        <NavigationContext.Provider value={navigationValue}>
          <UtilityContext.Provider value={utilityValue}>
            <SandboxSessionProvider>
              {/* No Internet Overlay Page */}
              <NoInternetOverlay />

              {/* Fixed Header on top */}
              <Header />

                {/* Only render Sidebar when user is logged in */}
                {user && isSidebarVisible && (
                  <div
                    className="fixed left-0 z-40 bg-white border-r border-gray-200"
                    style={{
                      top: "72px",
                      height: "calc(100vh - 72px)",
                      width: "16rem",
                      marginTop: "0",
                      position: "fixed",
                    }}
                  >
                    <MainSidebar />
                  </div>
                )}

              <main
                className={`${
                  user && isSidebarVisible ? "ml-64 pt-[72px]" : ""
                }  min-h-screen bg-background`}
                style={{
                  paddingTop: user && !isSidebarVisible ? "72px" : "",
                }}
              >
                <Routes key={user?.id || "logged-out"}>
                    <Route
                      path="/auth/callback"
                      element={<AuthCallbackPage />}
                    />
                    <Route path="/dashboard" element={<MoodleCallbackPage />} />
                    <Route
                      path="/sign-in"
                      element={
                        user ? (
                          <Navigate to="/" replace />
                        ) : (
                          <LoginForm data={configurationData} />
                        )
                      }
                    />
                    {/* Public page-not-found route accessible without authentication */}
                    <Route
                      path={paths.pageNotFound}
                      element={<PageNotFound />}
                    />
                    <Route
                      path="/*"
                      element={
                        isLoading ? (
                          <div className="flex items-center justify-center min-h-screen">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bits-blue"></div>
                          </div>
                        ) : user ? (
                          <DashboardRouter />
                        ) : (
                          <Navigate to="/sign-in" replace />
                        )
                      }
                    />
                </Routes>
                <Toaster position="bottom-left" />
              </main>
            </SandboxSessionProvider>
          </UtilityContext.Provider>
        </NavigationContext.Provider>
      </AuthContext.Provider>
    </NetworkStatusProvider>
  );
}
